import { useEffect, useRef, useState } from "react";
import { adjustKey, PACK_MAX, PACK_SIZES_MAX, PRICE_ADJUST_MAX, PURCHASE_MODES, quoteBoth, readPackSizes, type PriceSettings, type PublishedPrices, type PurchaseMode, type Quote } from "../server/constants";
import type { Settings } from "./api";
import { apiFetch } from "./viewer";

type Report = {
  inputs: {
    provider: string;
    videoPerStepUsd: number;
    llmPerStepUsd: number;
    llmMeasured: boolean;
    llmSamples: number;
    costPerGenerationUsd: number;
    stepsPerGame: number;
    stepsMeasured: boolean;
    stepSamples: number;
    windowDays: number;
  };
  stripe: { enabled: boolean; testMode: boolean; hasSecretKey: boolean; hasWebhookSecret: boolean };
  sales: { purchases: number; revenue_cents: number | null; buyers: number } | null;
  /** What players are charged right now. */
  live: { units: number; priceUsd: number }[];
  published: PublishedPrices | null;
  /** The settings would charge something else: nothing changes for players until they are published. */
  stale: boolean;
};

const MODE_LABELS: Record<PurchaseMode, string> = { off: "Off", generations: "Per gen", games: "Per game" };

/** Sub-cent LLM costs need more places than a price does. */
const usd = (n: number, places = 2) => `${n < 0 ? "−" : ""}$${Math.abs(n).toFixed(places)}`;

/** The fields typed into here, kept as text while editing so "-", "0." and a half-typed list survive. */
type Draft = Record<NumberField, string> & Record<ListField, string> & { adjust: Record<string, string> };
const NUMBER_FIELDS = ["profitPerGenerationUsd", "stripeFeePercent", "stripeFeeFixedUsd"] as const;
/** The two pack lists, typed as comma-separated text. */
const LIST_FIELDS = ["packGenerations", "packGames"] as const;
type ListField = (typeof LIST_FIELDS)[number];
type NumberField = (typeof NUMBER_FIELDS)[number];
const toDraft = (s: Settings): Draft => ({
  ...(Object.fromEntries(NUMBER_FIELDS.map(k => [k, String(s[k])])) as Record<NumberField, string>),
  packGenerations: s.packGenerations.join(", "),
  packGames: s.packGames.join(", "),
  adjust: Object.fromEntries(Object.entries(s.priceAdjustUsd ?? {}).map(([k, v]) => [k, String(v)])),
});

/**
 * Paid top-ups: which model is on sale, and what it costs. The price is never typed in - it is worked out from
 * the profit wanted per generation, over the measured cost of a generation and Stripe's fee, and the
 * breakdown underneath shows every number that went into it so a loss is visible before it is charged.
 *
 * The breakdown is repriced in the browser on every keystroke, with the same quotePack the server charges by;
 * only the costs come from the server, and those change with the provider, not with these fields.
 */
export function AdminPricing({ settings, password, save }: { settings: Settings; password: string; save: (patch: Partial<Settings>) => void }) {
  const [costs, setCosts] = useState<Report | null>(null);
  const [error, setError] = useState("");
  const [publishing, setPublishing] = useState(false);
  const [draft, setDraft] = useState<Draft>(() => toDraft(settings));
  /** Saves waiting for typing to pause, one per field, so moving to the next field never cancels the last. */
  const pending = useRef(new Map<keyof PriceSettings, ReturnType<typeof setTimeout>>());

  // What the server now holds replaces the draft - unless a save of the draft is still waiting to go.
  useEffect(() => {
    if (!pending.current.size) setDraft(toDraft(settings));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...NUMBER_FIELDS.map(k => settings[k]), ...LIST_FIELDS.map(k => settings[k].join(",")), JSON.stringify(settings.priceAdjustUsd ?? {})]);

  useEffect(() => {
    apiFetch("/api/admin/pricing", { method: "POST", body: JSON.stringify({ password }) })
      .then(r => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then(r => {
        setCosts(r);
        setError("");
      })
      .catch(err => setError((err as Error).message));
  }, [password, settings.videoProvider, settings.liveVideo, settings.maskyDraft, settings.promptsTillSuccess, settings.purchaseMode]);

  /** A typed value as a number, or the saved one while the text is not a number yet ("-", ""). */
  const numberOf = (key: NumberField) => {
    const n = Number(draft[key]);
    return draft[key].trim() !== "" && Number.isFinite(n) ? n : settings[key];
  };
  const live: PriceSettings = {
    // Sanitised the same way the server will: what is priced here is what would be charged.
    packGenerations: readPackSizes(draft.packGenerations.split(","), settings.packGenerations),
    packGames: readPackSizes(draft.packGames.split(","), settings.packGames),
    profitPerGenerationUsd: numberOf("profitPerGenerationUsd"),
    stripeFeePercent: Math.min(99, Math.max(0, numberOf("stripeFeePercent"))),
    stripeFeeFixedUsd: Math.max(0, numberOf("stripeFeeFixedUsd")),
    priceAdjustUsd: Object.fromEntries(
      Object.entries(draft.adjust)
        .map(([key, text]) => [key, Math.round((Number(text) || 0) * 100) / 100] as const)
        .filter(([, n]) => n !== 0),
    ),
  };

  const edit = (key: NumberField | ListField, text: string) => {
    const next = { ...draft, [key]: text };
    setDraft(next);
    // Saved once typing pauses, not per keystroke: each save is a request and a settings-file write.
    clearTimeout(pending.current.get(key));
    pending.current.set(
      key,
      setTimeout(() => {
        pending.current.delete(key);
        if (key === "packGenerations" || key === "packGames") {
          const sizes = readPackSizes(text.split(","), settings[key]);
          if (sizes.join(",") !== settings[key].join(",")) save({ [key]: sizes });
          return;
        }
        const n = Number(text);
        if (text.trim() !== "" && Number.isFinite(n) && n !== settings[key]) save({ [key]: n });
      }, 600),
    );
  };

  const number = (key: NumberField, opts: { step: number; min?: number; max?: number; width?: string; label: string }) => (
    <input
      type="number"
      step={opts.step}
      min={opts.min}
      max={opts.max}
      value={draft[key]}
      aria-label={opts.label}
      onChange={e => edit(key, e.target.value)}
      className={`${opts.width ?? "w-16"} rounded border border-white/15 bg-white/5 px-1.5 py-0.5 text-right text-xs text-white`}
    />
  );

  /**
   * The ± on one pack, in dollars. Typed into the draft so the price beside it moves as you type; the save is
   * debounced like the other fields, and neither reaches a player until the policy is published.
   */
  const adjust = (key: string) => (
    <input
      type="number"
      step={0.01}
      min={-PRICE_ADJUST_MAX}
      max={PRICE_ADJUST_MAX}
      value={draft.adjust[key] ?? ""}
      placeholder="±0"
      aria-label={`Price adjustment for ${key}`}
      title="Added to the computed price, to land on a friendlier number"
      onChange={e => editAdjust(key, e.target.value)}
      className="w-14 rounded border border-white/15 bg-white/5 px-1 py-0.5 text-right text-[10px] text-white placeholder:text-white/25"
    />
  );

  const editAdjust = (key: string, text: string) => {
    const next = { ...draft.adjust, [key]: text };
    setDraft(d => ({ ...d, adjust: next }));
    clearTimeout(pending.current.get("priceAdjustUsd"));
    pending.current.set(
      "priceAdjustUsd",
      setTimeout(() => {
        pending.current.delete("priceAdjustUsd");
        const map = Object.fromEntries(
          Object.entries(next)
            .map(([k, v]) => [k, Math.round((Number(v) || 0) * 100) / 100] as const)
            .filter(([, n]) => n !== 0),
        );
        if (JSON.stringify(map) !== JSON.stringify(settings.priceAdjustUsd ?? {})) save({ priceAdjustUsd: map });
      }, 600),
    );
  };

  /** Puts back the settings the live prices were published from, undoing an unpublished draft. */
  const reset = () => {
    const policy = costs?.published?.policy;
    if (!policy) return;
    pending.current.forEach(clearTimeout);
    pending.current.clear();
    setDraft(toDraft({ ...settings, ...policy }));
    save(policy);
  };

  const publish = async () => {
    setPublishing(true);
    try {
      const res = await apiFetch("/api/admin/publish-pricing", { method: "POST", body: JSON.stringify({ password }) });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const out = await res.json();
      setCosts(c => (c ? { ...c, live: out.live, published: out.published, stale: out.stale } : c));
      setError("");
    } catch (err) {
      setError((err as Error).message);
    }
    setPublishing(false);
  };

  const row = (label: string, control: React.ReactNode) => (
    <label className="mt-1 flex items-center justify-between gap-3 text-xs text-white/60">
      <span>{label}</span>
      {control}
    </label>
  );

  return (
    <div className="mb-3">
      <div className="text-white/70">Paid top-ups (Stripe)</div>
      <div className="mt-1 grid grid-cols-3 overflow-hidden rounded border border-white/15">
        {PURCHASE_MODES.map(mode => (
          <button
            key={mode}
            onClick={() => save({ purchaseMode: mode })}
            className={`py-1.5 text-xs uppercase tracking-widest transition ${
              settings.purchaseMode === mode ? "bg-sodium font-semibold text-black" : "text-white/60 hover:bg-white/10"
            }`}
          >
            {MODE_LABELS[mode]}
          </button>
        ))}
      </div>

      {row(
        "Scene pack sizes",
        <input
          value={draft.packGenerations}
          aria-label="Scene pack sizes"
          onChange={e => edit("packGenerations", e.target.value)}
          placeholder="10, 50, 100, 500"
          className="w-28 rounded border border-white/15 bg-white/5 px-1.5 py-0.5 text-right text-xs text-white"
        />,
      )}
      {row(
        "Story pack sizes",
        <input
          value={draft.packGames}
          aria-label="Story pack sizes"
          onChange={e => edit("packGames", e.target.value)}
          placeholder="1, 5, 10, 20, 50, 100"
          className="w-28 rounded border border-white/15 bg-white/5 px-1.5 py-0.5 text-right text-xs text-white"
        />,
      )}
      <div className="text-[11px] text-white/35">
        Up to {PACK_SIZES_MAX} sizes each, {PACK_MAX} per pack, offered together in whichever mode is on. Same profit per
        generation in every one, so the big packs come out cheaper each on their own.
      </div>
      {row("Profit per generation ($, − = loss)", number("profitPerGenerationUsd", { step: 0.01, label: "Profit per generation" }))}
      {row(
        "Stripe fee (incl. Managed Payments)",
        <span className="flex items-center gap-1 text-[11px] text-white/40">
          {number("stripeFeePercent", { step: 0.1, min: 0, max: 20, width: "w-12", label: "Stripe percent fee" })}% +
          {number("stripeFeeFixedUsd", { step: 0.01, min: 0, max: 5, width: "w-12", label: "Stripe fixed fee" })}
        </span>,
      )}

      <div className="mt-1 text-[11px] leading-relaxed text-white/35">
        6.4% = 2.9% card processing + 3.5% Managed Payments (on by default on new Stripe accounts; it handles sales
        tax, fraud and disputes). Turn Managed Payments off in Stripe and this becomes 2.9%.
      </div>
      {error && <div className="mt-2 text-xs text-siren-red">Couldn't price: {error}</div>}
      {costs && (
        <>
          <Breakdown
            report={costs}
            quotes={quoteBoth(costs.inputs.costPerGenerationUsd, costs.inputs.stepsPerGame, live)}
            active={settings.purchaseMode}
            adjust={adjust}
          />
          <Publish
            report={costs}
            draft={(settings.purchaseMode === "games"
              ? quoteBoth(costs.inputs.costPerGenerationUsd, costs.inputs.stepsPerGame, live).games
              : quoteBoth(costs.inputs.costPerGenerationUsd, costs.inputs.stepsPerGame, live).generations
            ).map(q => ({ units: q.units, priceUsd: q.priceUsd }))}
            publishing={publishing}
            onPublish={publish}
            onReset={reset}
            disabled={settings.purchaseMode === "off"}
          />
        </>
      )}
    </div>
  );
}

function Breakdown({
  report,
  quotes,
  active,
  adjust,
}: {
  report: Report;
  quotes: ReturnType<typeof quoteBoth>;
  active: PurchaseMode;
  adjust: (key: string) => React.ReactNode;
}) {
  const { inputs, stripe, sales } = report;
  const line = (label: string, value: string, tone = "text-white/70") => (
    <div className="flex justify-between gap-2">
      <span className="text-white/40">{label}</span>
      <span className={tone}>{value}</span>
    </div>
  );
  const quote = (title: string, q: Quote, unit: string, on: boolean) => {
    const tone = q.profitUsd < 0 ? "text-siren-red" : "text-teal";
    return (
      <div className={`rounded border p-2 ${on ? "border-sodium/60 bg-sodium/5" : "border-white/10"}`}>
        <div className="mb-1 flex items-center justify-between gap-2 text-[11px]">
          <span className={on ? "font-semibold text-sodium" : "text-white/60"}>{title}</span>
          <span className="flex items-center gap-1">
            {adjust(adjustKey(q.mode, q.units))}
            <span className="w-14 text-right text-white">{usd(q.priceUsd)}</span>
          </span>
        </div>
        {/* Where the price came from, when it is not simply the arithmetic. */}
        {q.adjustUsd !== 0 && line("Computed, then nudged", `${usd(q.computedUsd)} ${q.adjustUsd > 0 ? "+" : "−"} ${usd(Math.abs(q.adjustUsd))}`)}
        {line("Stripe fee", `−${usd(q.stripeFeeUsd)}`)}
        {line("Generation cost", `−${usd(q.costUsd)}`)}
        {line(q.profitUsd < 0 ? "Loss per purchase" : "Profit per purchase", usd(q.profitUsd), tone)}
        {line(`… per ${unit}`, usd(q.profitPerUnitUsd), tone)}
        {unit !== "generation" && line("… per generation", usd(q.profitPerGenerationUsd, 3), tone)}
      </div>
    );
  };

  return (
    <div className="mt-2 space-y-2 font-mono text-[10px]">
      <div className="rounded border border-white/10 p-2">
        <div className="mb-1 text-[11px] text-white/60">Cost of one generation</div>
        {line(`Video (${inputs.provider}, list rate)`, usd(inputs.videoPerStepUsd, 3))}
        {line(`LLM on GMI (${inputs.llmMeasured ? `avg of ${inputs.llmSamples} steps, ${inputs.windowDays}d` : "estimate, too few steps"})`, usd(inputs.llmPerStepUsd, 4))}
        {line("Total per generation", usd(inputs.costPerGenerationUsd, 3), "text-white")}
        {line(
          `Steps per game (${inputs.stepsMeasured ? `avg of ${inputs.stepSamples} endings` : "full run, too few endings"})`,
          inputs.stepsPerGame.toFixed(1),
        )}
      </div>
      {/* Only the mode that is on sale is worth the space; the other is shown small, for comparison. */}
      {(active === "games" ? quotes.games : quotes.generations).map(q => (
        <div key={`${q.mode}-${q.units}`}>
          {quote(`${q.units} ${q.mode === "games" ? (q.units === 1 ? "story" : "stories") : q.units === 1 ? "scene" : "scenes"}`, q, q.mode === "games" ? "story" : "scene", true)}
        </div>
      ))}
      {active === "off" && (
        <div className="text-white/35">Purchasing is off. Turn on a mode above to price it.</div>
      )}
      <p className="leading-relaxed text-white/35">
        Price = (units × (cost + profit) + fixed fee) ÷ (1 − % fee), rounded up to the cent, $0.50 minimum. Archive
        reuse is not counted as a saving, so real margins run a little higher.
      </p>
      {!stripe.enabled ? (
        <div className="rounded border border-sodium/40 bg-sodium/10 p-2 text-sodium">
          <b>Not on sale yet.</b> Set {[!stripe.hasSecretKey && "STRIPE_SECRET_KEY", !stripe.hasWebhookSecret && "STRIPE_WEBHOOK_SECRET"].filter(Boolean).join(" and ")} on the
          server. Players see no offer until both are set.
        </div>
      ) : (
        line("Stripe", stripe.testMode ? "connected (test mode)" : "connected (live)", stripe.testMode ? "text-sodium" : "text-teal")
      )}
      {sales && sales.purchases > 0 && line("Sales so far", `${sales.purchases} from ${sales.buyers} buyer${sales.buyers === 1 ? "" : "s"} · ${usd((sales.revenue_cents ?? 0) / 100)}`)}
    </div>
  );
}

/**
 * Publishing the price list.
 *
 * Costs move on their own, so the computed prices drift; what players pay must not. Until this button is
 * pressed, a settings change is a draft - the panel shows what it would do, and the live prices stay put.
 */
function Publish({
  report,
  draft,
  publishing,
  onPublish,
  onReset,
  disabled,
}: {
  report: Report;
  /** The prices the current settings would charge, i.e. what publishing would make live. */
  draft: { units: number; priceUsd: number }[];
  publishing: boolean;
  onPublish: () => void;
  onReset: () => void;
  disabled: boolean;
}) {
  const since = (iso: string) => {
    const mins = (Date.now() - Date.parse(iso)) / 60000;
    if (!Number.isFinite(mins)) return "at an unknown time";
    if (mins < 1) return "just now";
    if (mins < 60) return `${Math.round(mins)} min ago`;
    const hours = mins / 60;
    return hours < 24 ? `${Math.round(hours)}h ago` : `${Math.round(hours / 24)}d ago`;
  };
  const priceOf = (list: { units: number; priceUsd: number }[], units: number) => list.find(p => p.units === units)?.priceUsd;

  return (
    <div className="mt-2 rounded border border-white/10 p-2 font-mono text-[10px]">
      <div className="flex items-center justify-between gap-2">
        <span className="text-white/40">{report.published ? `Live since ${since(report.published.at)}` : "Never published"}</span>
        {report.stale && <span className="text-sodium">draft differs</span>}
      </div>

      {/* Live price beside the draft, so it is clear what publishing would change for players. */}
      {report.stale && (
        <div className="mt-1 space-y-0.5">
          {draft.map(({ units, priceUsd }) => {
            const now = priceOf(report.live, units);
            const changed = now === undefined || Math.abs(now - priceUsd) > 0.004;
            return (
              <div key={units} className="flex justify-between gap-2">
                <span className="text-white/40">{units}</span>
                <span className={changed ? "text-sodium" : "text-white/40"}>
                  {now === undefined ? "new" : usd(now)}
                  {changed && ` → ${usd(priceUsd)}`}
                </span>
              </div>
            );
          })}
          {report.live
            .filter(p => !draft.some(d => d.units === p.units))
            .map(p => (
              <div key={`gone-${p.units}`} className="flex justify-between gap-2">
                <span className="text-white/40">{p.units}</span>
                <span className="text-siren-red">{usd(p.priceUsd)} → withdrawn</span>
              </div>
            ))}
        </div>
      )}

      {/* Undo an unpublished draft: puts back the settings the live prices came from. */}
      {report.stale && report.published?.policy && (
        <button
          onClick={onReset}
          className="mt-2 w-full rounded border border-white/15 px-2 py-1.5 font-display text-[11px] font-bold tracking-widest text-white/60 transition hover:border-teal hover:text-teal"
        >
          RESET TO PUBLISHED POLICY
        </button>
      )}

      <button
        onClick={onPublish}
        disabled={publishing || disabled}
        className={`mt-2 w-full rounded px-2 py-1.5 font-display text-[11px] font-bold tracking-widest transition disabled:opacity-40 ${
          report.stale || !report.published ? "bg-sodium text-black hover:brightness-110" : "border border-white/15 text-white/60 hover:border-sodium hover:text-sodium"
        }`}
      >
        {publishing ? "PUBLISHING…" : "UPDATE PRICING POLICY"}
      </button>
      <p className="mt-1 leading-relaxed text-white/35">
        {report.published
          ? "Players are charged the published prices. Editing anything above only changes the draft; press this to make it real."
          : "Nothing is published yet, so players are charged the computed prices. Publish to freeze them."}
      </p>
    </div>
  );
}
